/**
 * Pure business logic for the "Sprawdź, czego potrzebujesz do PKK" tool —
 * deliberately separated from PkkChecklist.ts (the Lit UI) so the rules can
 * be read/tested without touching rendering. No I/O, no DOM, no storage.
 */

export type PkkResidence = 'polish' | 'foreign' | 'ukr' | 'student';
export type PkkAgeGroup = 'under18' | '18plus';
export type PkkLicenseType = 'first' | 'additional';

export interface PkkApplicant {
    ageGroup: PkkAgeGroup;
    residence: PkkResidence;
    licenseType: PkkLicenseType;
    hasMedicalCertificate: boolean;
    hasPhoto: boolean;
}

export type PkkItemStatus = 'have' | 'missing' | 'always';

export interface PkkRequirementItem {
    id: string;
    label: string;
    status: PkkItemStatus;
}

export interface PkkRequirementsResult {
    items: PkkRequirementItem[];
    allMet: boolean;
    nextStep: string;
}

export interface PkkRequirementsMessages {
    studyCertificate: string;
    foreignResidence: string;
    ukrStatus: string;
    medical: string;
    photo: string;
    application: string;
    identity: string;
    consent: string;
    licenceCopy: string;
    getMedical: string;
    getPhoto: string;
    applyWithParent: string;
    prepareDocuments: string;
}

export function getPkkRequirements(applicant: PkkApplicant, messages: PkkRequirementsMessages): PkkRequirementsResult {
    const items: PkkRequirementItem[] = [
        {
            id: 'medical-certificate',
            label: messages.medical,
            status: applicant.hasMedicalCertificate ? 'have' : 'missing',
        },
        {
            id: 'photo',
            label: messages.photo,
            status: applicant.hasPhoto ? 'have' : 'missing',
        },
        {
            id: 'application-form',
            label: messages.application,
            status: 'always',
        },
        {
            id: 'id-document',
            label: messages.identity,
            status: 'always',
        },
    ];

    if (applicant.residence !== 'polish') {
        items.push({
            id: 'residence-document',
            label:
                applicant.residence === 'ukr'
                    ? messages.ukrStatus
                    : applicant.residence === 'student'
                      ? messages.studyCertificate
                      : messages.foreignResidence,
            status: 'always',
        });
    }

    if (applicant.ageGroup === 'under18') {
        items.push({
            id: 'parental-consent',
            label: messages.consent,
            status: 'always',
        });
    }

    if (applicant.licenseType === 'additional') {
        items.push({
            id: 'existing-licence-copy',
            label: messages.licenceCopy,
            status: 'always',
        });
    }

    const missing = items.filter((item) => item.status === 'missing');
    const allMet = missing.length === 0;

    return {
        items,
        allMet,
        nextStep: getNextStep(missing, applicant, messages),
    };
}

function getNextStep(
    missing: PkkRequirementItem[],
    applicant: PkkApplicant,
    messages: PkkRequirementsMessages,
): string {
    const missingIds = new Set(missing.map((item) => item.id));

    if (missingIds.has('medical-certificate')) {
        return messages.getMedical;
    }

    if (missingIds.has('photo')) {
        return messages.getPhoto;
    }

    if (applicant.ageGroup === 'under18') {
        return messages.applyWithParent;
    }

    return messages.prepareDocuments;
}
